import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import { toast } from '@cotera/client/app/components/ui';
import { Assert } from '@cotera/utilities';
import { GraphModel } from '@cotera/client/v0/graph/graph.model';
import { DG } from '@cotera/sdk/core';
import { CoteraDuckDB, DuckDBQueryResult } from '@cotera/client/app/etc/duckdb';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { DataGridModel } from '@cotera/client/v0/data-grid';
import { AtcCellModel } from './cell.model';
import { v4 } from 'uuid';

export class RunAtcCellAction extends BaseAction<
  {},
  { t: string; message?: string }[]
> {
  title = 'Run ATC Cell';
  icon = 'play';
  discrete = true;
  inputSchema = z.object({});

  constructor(
    private readonly db: Promise<CoteraDuckDB>,
    private readonly client: TenantedClient['atc']
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(AtcCellModel);
  }

  async execute(
    _: {},
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(AtcCellModel);
    const graphModel = context.getInScopeModelOfType(GraphModel);
    const dataGridModel = context.targetedModelOfType(DataGridModel);

    Assert.assert(model !== null);
    Assert.assert(graphModel !== null);
    Assert.assert(dataGridModel !== null);

    const columnName = model.columnName;
    const graph = graphModel.graph.snapshot();
    const preview = dataGridModel.dataRel.snapshot().select((t) => t.star(), {
      offset: model.rowIndex,
      limit: 1,
    });

    if (preview === null) {
      return err([{ t: 'error', message: 'No preview data available' }]);
    }

    const config = model.llmConfig;

    // Query DuckDB to get all preview rows
    const db = await this.db;
    const arrowTable = await db.query(preview);
    const queryResult = new DuckDBQueryResult(arrowTable, preview.attributes);
    const rows = queryResult.toArray();

    if (rows.length === 0) {
      toast.info('No rows to run previews for');
      return ok({});
    }

    const editor = new DG.DocumentEditor.EditorState(config.editorState);

    // Get the expression for the column to compute messages
    const expr = editor.getExpression(
      graph,
      graph.parser.forRel(model.relName)
    );

    if (expr.isErr()) {
      return err([{ t: 'error', message: 'Failed to parse expression' }]);
    }

    // Build a relation with just the column expression
    const relWithColumn = preview.select((_) => ({
      [columnName]: expr.value,
    }));

    // Query to get messages for all rows
    const messagesTable = await db.query(relWithColumn);
    const messagesResult = new DuckDBQueryResult(
      messagesTable,
      relWithColumn.attributes
    );

    const messages = messagesResult.row(0).column(columnName).valueAt(0);

    const stableId = model.stableId.snapshot();
    const relIdentity = model.relIdentity.snapshot();
    const colIdentity = model.colIdentity.snapshot();

    const primaryKeyValue = rows[0]?.['__pk'];
    const completionId =
      model.isPreviewMode === false &&
      primaryKeyValue !== null &&
      primaryKeyValue !== undefined
        ? String(primaryKeyValue)
        : null;

    await this.client.runColumnValue({
      config,
      relIdentity: relIdentity?.toString() ?? '',
      colIdentity: colIdentity?.toString() ?? '',
      stableId: stableId ?? '',
      messages,
      completionId,
    });

    model.fetchKey.set(v4());

    return ok({});
  }
}
